import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Copy, Image as ImageIcon } from "lucide-react";
import { Button } from "@core/components/ui/button";
import { useToast } from "@core/hooks/use-toast";
import AddToCalendarMenu from "./AddToCalendarMenu";
import type { FinalDateEvent } from "@core/lib/add-to-calendar";
import {
  createYourOwnUrl,
  differsFromBrowserZone,
  formatFinalDate,
  type FinalDate,
} from "@core/lib/final-date";
import { shareFinalDateCard } from "@core/lib/final-date-card";
import { trackFinalDate } from "@core/services/FinalDateService";

interface FinalDateCardProps {
  poll: { id: string; title?: string | null; slug?: string | null };
  finalDate: FinalDate;
  /** voter : page du votant ; owner : page résultats de l'organisateur. */
  variant?: "voter" | "owner";
  className?: string;
}

/** Description du .ics / des liens d'agenda : annonce + lien + signature ref=ics. */
export function buildCalendarEvent(
  poll: FinalDateCardProps["poll"],
  finalDate: FinalDate,
  footer: string,
  origin = typeof window !== "undefined" ? window.location.origin : "https://doodates.com",
): FinalDateEvent {
  const url = poll.slug ? `${origin}/poll/${poll.slug}/vote` : undefined;
  return {
    uid: poll.id,
    sequence: finalDate.sequence ?? 0,
    title: poll.title?.trim() || "DooDates",
    description: [finalDate.announcement, footer].filter(Boolean).join("\n\n"),
    url,
    slot: finalDate,
  };
}

/**
 * Bloc « Date retenue » : jour et heure, annonce, « Ajouter à mon
 * calendrier », carte image à partager et « Faites voter votre groupe → ».
 */
export const FinalDateCard: React.FC<FinalDateCardProps> = ({
  poll,
  finalDate,
  variant = "voter",
  className = "",
}) => {
  const { t, i18n } = useTranslation("polls");
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const locale = i18n.language || "fr";
  const labels = useMemo(() => formatFinalDate(finalDate, locale), [finalDate, locale]);
  const showZone = differsFromBrowserZone(finalDate);

  const event = useMemo(
    () =>
      buildCalendarEvent(
        poll,
        finalDate,
        t("final_date.ics_footer", { url: createYourOwnUrl("ics") }),
      ),
    [poll, finalDate, t],
  );

  const copyAnnouncement = async () => {
    if (!finalDate.announcement) return;
    try {
      await navigator.clipboard.writeText(finalDate.announcement);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackFinalDate("final_date_share", poll.id, { mode: "copy", variant });
    } catch {
      toast({ title: t("final_date.copy_failed"), variant: "destructive" });
    }
  };

  const shareImage = async () => {
    const day = labels.day.charAt(0).toUpperCase() + labels.day.slice(1);
    const when = labels.time ? `${day} · ${labels.time}` : day;
    const count =
      typeof finalDate.yes === "number" && finalDate.total
        ? t("final_date.card_count", { count: finalDate.yes, total: finalDate.total })
        : undefined;
    const mode = await shareFinalDateCard(
      {
        headline: t("final_date.card_headline", { weekday: labels.weekday }),
        when,
        count,
        footer: "doodates.com",
      },
      finalDate.announcement || "",
    );
    trackFinalDate("final_date_share", poll.id, { mode: `image_${mode}`, variant });
    if (mode === "downloaded") toast({ title: t("final_date.image_downloaded") });
    if (mode === "failed") toast({ title: t("final_date.image_failed"), variant: "destructive" });
  };

  return (
    <section
      data-testid="final-date-card"
      className={`rounded-2xl border border-success/40 bg-card p-5 sm:p-6 shadow-sm ${className}`}
    >
      <div className="flex flex-col items-center text-center gap-2">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-success">
          <Check className="h-6 w-6" aria-hidden />
        </div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("final_date.retained_label")}
        </p>
        <h2
          className="text-xl sm:text-2xl font-bold text-foreground break-words max-w-full first-letter:uppercase"
          data-testid="final-date-day"
        >
          {/* Plage formatée dans la langue du votant, pas le libellé stocké
              (« Week-end du 17-18 oct » est figé en français à la création). */}
          {labels.day}
        </h2>
        {labels.time ? (
          <p className="text-lg font-semibold text-foreground tabular-nums" data-testid="final-date-time">
            {labels.time}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">{t("final_date.all_day")}</p>
        )}
        {showZone && (
          <p className="text-xs text-muted-foreground" data-testid="final-date-zone">
            {t("final_date.organizer_timezone", { zone: finalDate.timezone })}
          </p>
        )}
        {typeof finalDate.yes === "number" && !!finalDate.total && (
          <p className="text-sm text-muted-foreground">
            {t("final_date.available_count", { count: finalDate.yes, total: finalDate.total })}
          </p>
        )}
      </div>

      {finalDate.announcement && (
        <blockquote
          className="mt-4 rounded-xl bg-muted/60 p-4 text-sm text-foreground whitespace-pre-line break-words"
          data-testid="final-date-announcement"
        >
          {finalDate.announcement}
        </blockquote>
      )}

      <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-center gap-2">
        <AddToCalendarMenu event={event} pollId={poll.id} source={variant} />
        {finalDate.announcement && (
          <Button variant="outline" onClick={copyAnnouncement} data-testid="final-date-copy">
            {copied ? <Check className="h-4 w-4 mr-2" aria-hidden /> : <Copy className="h-4 w-4 mr-2" aria-hidden />}
            {copied ? t("final_date.copied") : t("final_date.copy_announcement")}
          </Button>
        )}
        <Button variant="outline" onClick={shareImage} data-testid="final-date-share-image">
          <ImageIcon className="h-4 w-4 mr-2" aria-hidden />
          {t("final_date.share_image")}
        </Button>
      </div>

      <div className="mt-5 border-t border-border pt-4 text-center">
        <a
          href={createYourOwnUrl("final", typeof window !== "undefined" ? window.location.origin : undefined)}
          onClick={() => trackFinalDate("final_date_create_cta", poll.id, { ref: "final", variant })}
          className="inline-flex items-center text-sm font-medium text-primary hover:underline"
          data-testid="final-date-create-cta"
        >
          {t("final_date.create_cta")}
          <ArrowRight className="h-4 w-4 ml-1" aria-hidden />
        </a>
      </div>
    </section>
  );
};

export default FinalDateCard;
